import React, { Component, ErrorInfo, ReactNode } from 'react';
import { toast } from 'react-hot-toast';

// Define logger interface to make it extensible
interface ErrorLogger {
  logError(error: Error, errorInfo: ErrorInfo, componentStack?: string): void;
}

// Default console logger implementation
class ConsoleErrorLogger implements ErrorLogger {
  logError(error: Error, errorInfo: ErrorInfo, componentStack?: string): void {
    // Log to console with distinct formatting
    console.group('%c💥 React Error Boundary Caught Error', 'color:red; font-size:12px; font-weight:bold;');
    console.error('Error:', error);
    console.error('Component Stack:', errorInfo.componentStack);
    console.groupEnd();
    
    // This would be where you'd send to a service like Sentry
    // Example: Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
  }
}

interface Props {
  children?: ReactNode;
  logger?: ErrorLogger;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  isRetrying: boolean;
  retryCount: number;
}

class ErrorBoundary extends Component<Props, State> {
  private logger: ErrorLogger;
  
  constructor(props: Props) {
    super(props);
    this.logger = props.logger || new ConsoleErrorLogger();
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isRetrying: false,
      retryCount: 0,
    };
  }

  public static getDerivedStateFromError(error: Error): Pick<State, 'hasError'> {
    return { hasError: true };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });
    
    // Log the error using the logger
    this.logger.logError(error, errorInfo);
  }

  private isNetworkError(): boolean {
    const { error } = this.state;
    if (!error) return false;

    return (
      error.message.includes('Failed to fetch') ||
      error.message.includes('NetworkError') ||
      error.message.includes('Network Error') ||
      error.message.includes('network') ||
      error.message.includes('ECONNREFUSED') ||
      error.message.includes('Connection refused')
    );
  }
  
  private isServerError(): boolean {
    const { error } = this.state;
    if (!error) return false;
    
    return (
      error.message.includes('500') ||
      error.message.includes('Internal Server Error') ||
      error.message.includes('503') ||
      error.message.includes('Service Unavailable')
    );
  }

  private handleRetry = () => {
    this.setState({ 
      isRetrying: true,
      retryCount: this.state.retryCount + 1
    });
    
    // Log retry attempt
    if (this.state.error) {
      this.logger.logError(
        new Error(`Retry attempt ${this.state.retryCount + 1} for: ${this.state.error.message}`),
        { componentStack: this.state.errorInfo?.componentStack || '' }
      );
    }
    
    // Show toast notification
    toast.promise(
      new Promise((resolve) => {
        // Attempt to reconnect
        setTimeout(() => {
          this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
            isRetrying: false,
          });
          resolve(true);
        }, 2000);
      }),
      {
        loading: 'Attempting to reconnect...',
        success: 'Reconnected successfully!',
        error: 'Failed to reconnect. Please try again.',
      }
    );
  };

  private handleReload = () => {
    // Log reload action
    if (this.state.error) {
      this.logger.logError(
        new Error(`Page reload requested for: ${this.state.error.message}`),
        { componentStack: this.state.errorInfo?.componentStack || '' }
      );
    }
    
    window.location.reload();
  };

  public render() {
    const { hasError, error, errorInfo, isRetrying, retryCount } = this.state;

    if (hasError) {
      const isNetwork = this.isNetworkError();
      const isServer = this.isServerError();

      // Auto-retry once for network errors
      if (isNetwork && retryCount === 0 && !isRetrying) {
        setTimeout(() => this.handleRetry(), 3000);
      }

      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="max-w-md w-full p-8 bg-white rounded-lg shadow-lg">
            <div className="flex justify-center mb-6">
              {isNetwork ? (
                <svg className="h-16 w-16 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636a9 9 0 010 12.728m-3.536-3.536a5 5 0 010-7.072m2.12-2.12a9 9 0 010 12.728M6.343 6.343a9 9 0 1112.728 0l-1.414 1.414a7 7 0 10-9.9 0l-1.414-1.414zm10.728 10.728a5 5 0 11-7.071 0" />
                  <line x1="1" y1="1" x2="23" y2="23" strokeWidth="2" />
                </svg>
              ) : isServer ? (
                <svg className="h-16 w-16 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                </svg>
              ) : (
                <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
                  <span className="text-red-600 text-2xl font-bold">!</span>
                </div>
              )}
            </div>
            
            <h2 className="text-2xl font-bold text-center mb-2">
              {isNetwork 
                ? "Connection Error" 
                : isServer 
                  ? "Server Error"
                  : "Something went wrong"}
            </h2>
            
            <p className="text-gray-600 text-center mb-6">
              {isNetwork 
                ? "We couldn't connect to the server. Please check your internet connection and try again." 
                : isServer
                  ? "Our servers are currently experiencing issues. We're working to fix this as soon as possible."
                  : "An unexpected error occurred while trying to process your request."}
            </p>

            <div className="flex flex-col gap-3">
              {isNetwork && (
                <button 
                  className="flex items-center justify-center gap-2 w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md font-medium transition-colors"
                  onClick={this.handleRetry}
                  disabled={isRetrying}
                >
                  {isRetrying ? 'Reconnecting...' : 'Try to reconnect'}
                  {isRetrying ? (
                    <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                    </svg>
                  )}
                </button>
              )}
              
              <button 
                className="flex items-center justify-center gap-2 w-full border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 py-2 px-4 rounded-md font-medium transition-colors"
                onClick={this.handleReload}
              >
                Reload page
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && (
              <div className="mt-6 p-4 bg-gray-100 rounded text-sm overflow-auto">
                <details>
                  <summary className="cursor-pointer text-gray-700 font-medium">Technical Details</summary>
                  <div className="mt-2">
                    <p className="text-red-600 font-mono">{error && error.toString()}</p>
                    <p className="mt-2 text-gray-600 font-mono whitespace-pre-wrap">
                      {errorInfo && errorInfo.componentStack}
                    </p>
                  </div>
                </details>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 
